/**
 * Intake BE(spnp) `/v1/probes/{uuid}/episodes/{source_id}/inspect` 경계.
 *
 * 응답은 점검 artifact 의 **목차**(엔진 manifest 원문)에 파일 · 영상별 읽기 URL 을 더한 것이다.
 * 값 배열은 본문에 없다 — 화면이 `artifacts[].url` 로 Parquet 를 직접 받는다(큰 정수 ns 와 NaN 을
 * JSON 으로 펴면 잃는다). episode 하나를 여는 데 목차 요청 하나 + 배열 요청 묶음 하나다.
 *
 * **미확인(`null`)의 의미는 이 경계가 소유한다**(규칙 §6) — `?? []` 로 접지 않는다:
 *
 * | 필드 | `null` 의 뜻 |
 * |---|---|
 * | `pose` | 원본이 pose 의미를 선언하지 않음 → 단위 · 회전 순서를 FE 가 정하지 않는다 |
 * | `source_rows` | 원본 파일 행 번호 미확인 → 배열 index 로 대체하지 않는다 |
 * | `presentation_seconds` | 재생 시각 대응 미확인 → 영상 위치는 합성이다 |
 * | `source_frames` | 원본 frame 번호 미확인 → 반복 · 누락을 판정하지 않는다 |
 * | `clock.id` | 시계 관계 미확인 → 다른 채널과 같은 시각으로 비교하지 않는다 |
 * | `time_origin.*` | 공통 원점 없음 → 공통 시간축 없이 채널별로만 본다 |
 * | `channels[].pose` | 원본이 pose 해석을 선언하지 않음 → 사람이 매핑에서 정한다 |
 *
 * `validity[i] === true` 는 "무효 표시가 없다" 이지 "검증 통과" 가 아니다.
 *
 * Owns:
 * - 응답 모양 확인과 `EpisodeInspect` 변환(판 `INSPECT_SCHEMA_VERSION` 확인), 배열 바이트 받기,
 *   취소(`AbortSignal`) 전달.
 *
 * Does not own:
 * - 배열 디코드 — `@/shared/parquet`. 오류 → 문구 — `feature/inspectErrorMessage.ts`.
 */
import {
  apiClient,
  IntakeContractError,
  readFiniteNumber,
  readInstantMs,
  readInteger,
  readList,
  readNullableFiniteNumber,
  readNullableString,
  readRecord,
  readString,
} from "@/shared/http";
import {
  INSPECT_SCHEMA_VERSION,
  type ClockUnit,
  type EpisodeInspect,
  type InspectArrayRef,
  type InspectArtifactFile,
  type InspectChannelDeclaration,
  type InspectClockRef,
  type InspectIssue,
  type InspectStream,
  type InspectTimeOrigin,
  type InspectVideo,
  type PoseDeclaration,
} from "../domain/episodeInspect";

const RESOURCE = "inspect";
const CLOCK_UNITS: readonly string[] = ["s", "ns"];

export async function getEpisodeInspect(
  probeUuid: string,
  sourceId: string,
  signal?: AbortSignal,
): Promise<EpisodeInspect> {
  const response = await apiClient.get<unknown>(
    `/v1/probes/${probeUuid}/episodes/${encodeURIComponent(sourceId)}/inspect`,
    { signal },
  );
  return parseEpisodeInspect(response.data);
}

/**
 * 배열 파일 하나의 바이트. URL 은 토큰이 곧 권한이라 Bearer 를 붙이지 않는다 — 그래서 앱
 * client 가 아니라 fetch 로 받는다.
 */
export async function fetchInspectArray(url: string, signal?: AbortSignal): Promise<ArrayBuffer> {
  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new IntakeContractError(RESOURCE, `artifact:${response.status}`);
  }
  return response.arrayBuffer();
}

export function parseEpisodeInspect(value: unknown): EpisodeInspect {
  const body = readRecord(value, RESOURCE, "body");
  const inspection = readRecord(body.inspection, RESOURCE, "inspection");
  const streams = readList(inspection.streams, RESOURCE, "inspection.streams");
  // 판이 다르면 필드의 뜻이 다를 수 있다. 그래서 다른 판은 읽지 않고 오류로 멈춘다.
  const schemaVersion = readInteger(body.schema_version, RESOURCE, "schema_version");
  if (schemaVersion !== INSPECT_SCHEMA_VERSION) {
    throw new IntakeContractError(RESOURCE, "schema_version");
  }

  return {
    schemaVersion,
    sourceId: readString(body.source_id, RESOURCE, "source_id"),
    kind: readString(inspection.kind, RESOURCE, "inspection.kind"),
    sourceFingerprint: readNullableString(
      inspection.source_fingerprint,
      RESOURCE,
      "inspection.source_fingerprint",
    ),
    engineRevision: readString(inspection.engine_revision, RESOURCE, "inspection.engine_revision"),
    timeOrigin: parseTimeOrigin(body.time_origin),
    streams: streams.map((stream, index) => parseStream(stream, index)),
    channels: readList(body.channels, RESOURCE, "channels").map((channel, index) =>
      parseDeclaration(channel, index),
    ),
    issues: readList(inspection.issues, RESOURCE, "inspection.issues").map((issue, index) =>
      parseIssue(issue, index),
    ),
    sourceMetadata: readRecord(inspection.source_metadata, RESOURCE, "inspection.source_metadata"),
    videos: readList(body.videos, RESOURCE, "videos").map((video, index) =>
      parseVideo(video, index),
    ),
    artifacts: readList(body.artifacts, RESOURCE, "artifacts").map((file, index) =>
      parseArtifactFile(file, index),
    ),
    mediaExpiresAtMs: readInstantMs(body.media_expires_at, RESOURCE, "media_expires_at"),
  };
}

function parseTimeOrigin(value: unknown): InspectTimeOrigin {
  const row = readRecord(value, RESOURCE, "time_origin");
  const unit = readNullableString(row.unit, RESOURCE, "time_origin.unit");
  if (unit !== null && !CLOCK_UNITS.includes(unit)) {
    throw new IntakeContractError(RESOURCE, "time_origin.unit");
  }
  return {
    channel: readNullableString(row.channel, RESOURCE, "time_origin.channel"),
    clockId: readNullableString(row.clock_id, RESOURCE, "time_origin.clock_id"),
    unit: unit as ClockUnit | null,
    value: readNullableString(row.value, RESOURCE, "time_origin.value"),
  };
}

function parseStream(value: unknown, index: number): InspectStream {
  const field = `inspection.streams[${index}]`;
  const row = readRecord(value, RESOURCE, field);
  const key = readString(row.key, RESOURCE, `${field}.key`);
  const base = {
    key,
    clock: parseClock(row.clock, `${field}.clock`),
    timestamps: parseArrayRef(row.timestamps, `${field}.timestamps`),
    validity: parseArrayRef(row.validity, `${field}.validity`),
  };
  const kind = readString(row.kind, RESOURCE, `${field}.kind`);
  if (kind === "vector") {
    return {
      ...base,
      kind: "vector",
      values: parseArrayRef(row.values, `${field}.values`),
      sourceRows: parseOptionalArrayRef(row.source_rows, `${field}.source_rows`),
      pose: parsePose(row.pose, `${field}.pose`),
    };
  }
  if (kind === "image") {
    return {
      ...base,
      kind: "image",
      sourcePath: readNullableString(row.source_path, RESOURCE, `${field}.source_path`),
      sourceFrames: parseOptionalArrayRef(row.source_frames, `${field}.source_frames`),
      presentationSeconds: parseOptionalArrayRef(
        row.presentation_seconds,
        `${field}.presentation_seconds`,
      ),
      videoValidation: readString(row.video_validation, RESOURCE, `${field}.video_validation`),
    };
  }
  throw new IntakeContractError(RESOURCE, `${field}.kind`);
}

function parseClock(value: unknown, field: string): InspectClockRef {
  const row = readRecord(value, RESOURCE, field);
  const unit = readString(row.unit, RESOURCE, `${field}.unit`);
  if (!CLOCK_UNITS.includes(unit)) {
    throw new IntakeContractError(RESOURCE, `${field}.unit`);
  }
  return { id: readNullableString(row.id, RESOURCE, `${field}.id`), unit: unit as ClockUnit };
}

function parseArrayRef(value: unknown, field: string): InspectArrayRef {
  const row = readRecord(value, RESOURCE, field);
  const shape = readList(row.shape, RESOURCE, `${field}.shape`);
  if (shape.length === 0 || shape.some((entry) => !Number.isInteger(entry))) {
    throw new IntakeContractError(RESOURCE, `${field}.shape`);
  }
  return {
    path: readString(row.path, RESOURCE, `${field}.path`),
    dtype: readString(row.dtype, RESOURCE, `${field}.dtype`),
    shape: shape as number[],
  };
}

/** 선언이 없을 수 있는 배열 — `null` 은 미확인이고 필드 부재는 계약 위반이다. */
function parseOptionalArrayRef(value: unknown, field: string): InspectArrayRef | null {
  if (value === null) return null;
  if (value === undefined) {
    throw new IntakeContractError(RESOURCE, field);
  }
  return parseArrayRef(value, field);
}

function parsePose(value: unknown, field: string): PoseDeclaration | null {
  if (value === null) return null;
  const row = readRecord(value, RESOURCE, field);
  return {
    referenceFrame: readString(row.reference_frame, RESOURCE, `${field}.reference_frame`),
    targetFrame: readString(row.target_frame, RESOURCE, `${field}.target_frame`),
    positionUnit: readString(row.position_unit, RESOURCE, `${field}.position_unit`),
    rotation: readString(row.rotation, RESOURCE, `${field}.rotation`),
  };
}

function parseIssue(value: unknown, index: number): InspectIssue {
  const field = `inspection.issues[${index}]`;
  const row = readRecord(value, RESOURCE, field);
  const rows = readList(row.rows, RESOURCE, `${field}.rows`);
  if (rows.some((entry) => !Number.isInteger(entry))) {
    throw new IntakeContractError(RESOURCE, `${field}.rows`);
  }
  return {
    channel: readNullableString(row.channel, RESOURCE, `${field}.channel`),
    code: readString(row.code, RESOURCE, `${field}.code`),
    message: readString(row.message, RESOURCE, `${field}.message`),
    rows: rows as number[],
    path: readNullableString(row.path, RESOURCE, `${field}.path`),
  };
}

function parseDeclaration(value: unknown, index: number): InspectChannelDeclaration {
  const field = `channels[${index}]`;
  const row = readRecord(value, RESOURCE, field);
  const dim = readInteger(row.dim, RESOURCE, `${field}.dim`);
  const names = row.names === null ? null : readStringList(row.names, `${field}.names`);
  if (names !== null && names.length !== dim) {
    throw new IntakeContractError(RESOURCE, `${field}.names`);
  }
  return {
    key: readString(row.key, RESOURCE, `${field}.key`),
    kind: readString(row.kind, RESOURCE, `${field}.kind`),
    dtype: readString(row.dtype, RESOURCE, `${field}.dtype`),
    dim,
    names,
    pose: parsePose(row.pose, `${field}.pose`),
  };
}

function parseVideo(value: unknown, index: number): InspectVideo {
  const field = `videos[${index}]`;
  const row = readRecord(value, RESOURCE, field);
  const shape = readList(row.declared_shape, RESOURCE, `${field}.declared_shape`);
  return {
    key: readString(row.key, RESOURCE, `${field}.key`),
    path: readString(row.path, RESOURCE, `${field}.path`),
    url: readString(row.url, RESOURCE, `${field}.url`),
    width: readInteger(row.width, RESOURCE, `${field}.width`),
    height: readInteger(row.height, RESOURCE, `${field}.height`),
    codec: readString(row.codec, RESOURCE, `${field}.codec`),
    fps: readFiniteNumber(row.fps, RESOURCE, `${field}.fps`),
    durationSeconds: readFiniteNumber(row.duration_seconds, RESOURCE, `${field}.duration_seconds`),
    frameCount: readInteger(row.frame_count, RESOURCE, `${field}.frame_count`),
    declaredShape: shape as number[],
    fromSeconds: readNullableFiniteNumber(row.from_seconds, RESOURCE, `${field}.from_seconds`),
    toSeconds: readNullableFiniteNumber(row.to_seconds, RESOURCE, `${field}.to_seconds`),
  };
}

function parseArtifactFile(value: unknown, index: number): InspectArtifactFile {
  const field = `artifacts[${index}]`;
  const row = readRecord(value, RESOURCE, field);
  return {
    path: readString(row.path, RESOURCE, `${field}.path`),
    size: readInteger(row.size, RESOURCE, `${field}.size`),
    sha256: readString(row.sha256, RESOURCE, `${field}.sha256`),
    url: readString(row.url, RESOURCE, `${field}.url`),
  };
}

function readStringList(value: unknown, field: string): readonly string[] {
  const list = readList(value, RESOURCE, field);
  if (list.some((entry) => typeof entry !== "string")) {
    throw new IntakeContractError(RESOURCE, field);
  }
  return list as string[];
}
